import React, { useState, useEffect } from 'react';
import useAxiosPrivateSAI from '../../../../hooks/useAxiosPrivateSAI';
import useAuth from '../../../../hooks/useAuth';
import _ from 'lodash';

const StepTwoEdit = ({data, onUpdate, conditionId, policy = null, mutualGbs, claim = null}) => {
  const axios = useAxiosPrivateSAI()
  const { setShowLoader } = useAuth()
  const [model, setModel] = useState(data?.model || {});
  const [answers, setAnswers] = useState({
    continuous: data?.model?.continuous_absence || null,
    contracted: data?.model?.twenty_working_days || null,
    pregnant: data?.model?.parental_leave_planned || null,
    suspended: data?.model?.upcoming_disciplinary_action || null,
    request: data?.model?.supporting_documentation || null,
  });

  const [condition, setCondition] = useState(null)
  const [declarations, setDeclarations] = useState([])
  const [deliverables, setDeliverables] = useState([])
  const [claimStatus, setClaimStatus] = useState(0)

  useEffect(() => {
    if (data) {
      setModel(data.model || {})
      setAnswers({
        continuous: data.model?.continuous_absence || null,
        contracted: data.model?.twenty_working_days || null,
        pregnant: data.model?.parental_leave_planned || null,
        suspended: data.model?.upcoming_disciplinary_action || null,
        request: data.model?.supporting_documentation || null,
      });
    }

    console.log(answers)
  }, [data]);

  const handleChange = (field, value) => {
    const updatedModel = { [field]: value }
  
    setModel(prev => ({ ...prev, ...updatedModel }))
    setAnswers(prev => ({ ...prev, [field]: value }))
  
    onUpdate && onUpdate({ ...answers, model: updatedModel })
  }

  const shouldShowDeliverable = (deliverable, model) => {
    if (!deliverable.parent_id) return true;
  
    const parentVal = model[deliverable.parent_id]
    const showOn = deliverable.show_on || deliverable.parents
  
    if (!parentVal || !showOn) return false;
  
    try {
      const showOnParsed = JSON.parse(showOn)
      return showOnParsed.includes(parentVal)
    } catch (err) {
      return false
    }
  }

  const getSchema = async () => {
    if (!conditionId || !policy?.policy_id) {
      console.warn('Missing conditionId or policy_id');
      return;
    }  

    setShowLoader(true)
    try {
      const res = await axios.get(`/api/conditions/${conditionId}`);
      const responseData = res.data;

      const fetchedCondition = responseData.condition || responseData;
      const fetchedDeclarations = responseData.declarations;
      let fetchedDeliverables = responseData.deliverables;

      setCondition(fetchedCondition);
      setDeclarations(fetchedDeclarations);

      if (policy?.policy_plus) {
        fetchedDeliverables = fetchedDeliverables.filter((d) => !d.policy_plus_hide);
      }

      const flatDeliverables = [];
      fetchedDeliverables.forEach((d) => {
        flatDeliverables.push(d);
        d.child_deliverables?.forEach((c) => {
          flatDeliverables.push(c);
          c.child_deliverables?.forEach((gc) => flatDeliverables.push(gc));
        });
      });

      const enrichedDeliverables = flatDeliverables.map((d) => ({
        ...d,
        label: mutualGbs ? d.alt_label : d.label,
        styleClasses: 'claim-form-deliverables',
        selectOptions: { hideNoneSelectedText: true },
        disabledpicker: d.type === 'date' && claimStatus >= 5,
        onChanged: (model, newVal, oldVal, field) => {
          console.log(`Changed: ${field.help}`);
        },
      }));

      const sortedDeliverables = _.sortBy(enrichedDeliverables, 'weight');
      setDeliverables(sortedDeliverables);
      const initialModel = { ...model };
      sortedDeliverables.forEach((d) => {
        if (!initialModel[d.model]) {
          initialModel[d.model] = d.type === 'radios' ? false : '';
        }
      });
      setModel(initialModel);
    } catch (err) {
      console.error('Error fetching schema:', err);
    } finally {
      setShowLoader(false)
    }
  };

  useEffect(() => {
    getSchema();
  }, [conditionId]);

  return (
    <div className="grid md:grid-cols-2 gap-8">
      {/* Left: Form Inputs */}
      <div>
        {deliverables
        .filter(d => shouldShowDeliverable(d, model))
        .map((deliverable) => {
          const id = deliverable.model;

          // Radios
          if (deliverable.type === 'radios') {
            return (
              <div key={id} className="mb-4">
                <label className="font-medium">{deliverable.label}</label>
                <div className="flex gap-4 mt-1">
                  {JSON.parse(deliverable.options || '["Yes","No"]').map((option) => (
                    <label key={option} className="inline-flex items-center">
                      <input
                        type="radio"
                        name={id}
                        value={option}
                        checked={model[id] === option}
                        onChange={(e) => handleChange(id, e.target.value)}
                      />
                      <span className="ml-2">{option}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          }

          // Selects
          if (deliverable.type === 'select') {
            return (
              <div key={id} className="mb-4">
                <label className="font-medium block mb-1">{deliverable.label}</label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  value={model[id] || ''}
                  onChange={(e) => handleChange(id, e.target.value)}
                >
                  <option value="">Please select</option>
                  {deliverable.values.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            );
          }

          return null;
        })}
      </div>

      {/* Right: Live Feedback */}
      <div className="border-l border-gray-300 px-4  text-gray-800 space-y-3">
        <p className="font-semibold mb-3">Please check the following statements about your payment request</p>
        {deliverables
          .filter(d => ['radios', 'select'].includes(d.type))
          .filter(d => model[d.model])
          .map((d) => {
            let feedback = '';

            if (d.type === 'radios') {
              try {
                const parsedHelp = JSON.parse(d.help || '{}');
                feedback = parsedHelp[model[d.model]]?.value || '';
              } catch {
                feedback = '';
              }
            }

            if (d.type === 'select') {
              feedback = `${d.help || ''}${model[d.model]}`;
            }

            return (
              <p key={d.deliverable_id} className="text-sas border-b pb-3">
                {feedback}
              </p>
            );
        })}
      </div>
    </div>
  );
};

export default StepTwoEdit;


